// When a modifier group has split_quantity = true the cart contract is
// "one selection per unit of the parent product". The picker UI caps inputs
// at the platter quantity, but if the user reduces the platter quantity
// after selecting, the existing selections are not auto-trimmed and the
// cart could be submitted with sum(modifier.quantity) > platterQuantity.
// This trims excess selections from the end of each violating group.

type ModifierEntry = {
	modifier_group_id: number;
	quantity: number;
	[key: string]: unknown;
};

type ModifierGroup = {
	id: number;
	split_quantity?: boolean;
};

export function clampSplitQuantityModifiers<T extends ModifierEntry>(
	modifiers: T[],
	groups: ModifierGroup[],
	platterQuantity: number,
): T[] {
	const byGroup = new Map<number, T[]>();
	for (const mod of modifiers) {
		const bucket = byGroup.get(mod.modifier_group_id) ?? [];
		bucket.push({ ...mod });
		byGroup.set(mod.modifier_group_id, bucket);
	}

	const result: T[] = [];
	for (const [groupId, mods] of byGroup) {
		const group = groups.find((g) => g.id === groupId);
		const total = mods.reduce(
			(sum, m) => sum + (Number(m.quantity) || 0),
			0,
		);

		const needsTrim = !!group?.split_quantity && total > platterQuantity;

		if (needsTrim) {
			let excess = total - platterQuantity;
			for (let i = mods.length - 1; i >= 0 && excess > 0; i--) {
				const q = Number(mods[i].quantity) || 0;
				if (q <= excess) {
					excess -= q;
					mods[i].quantity = 0;
				} else {
					mods[i].quantity = q - excess;
					excess = 0;
				}
			}
		}

		for (const m of mods) {
			if ((Number(m.quantity) || 0) > 0) result.push(m);
		}
	}

	return result;
}
